import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { AdminOrganization, AdminOrgMember, AdminOrganizationsResponse } from '@/types'
import { toast } from 'sonner'

const ORGS_QUERY_KEY = 'admin-organizations'

export const useAdminOrganizations = (search?: string) => {
  return useQuery<AdminOrganizationsResponse>({
    queryKey: [ORGS_QUERY_KEY, search],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50', offset: '0' })
      if (search) params.set('search', search)
      const { data } = await api.get(`/organizations?${params.toString()}`)
      return data
    },
  })
}

export const useAdminOrganization = (orgId: string) => {
  return useQuery<AdminOrganization>({
    queryKey: [ORGS_QUERY_KEY, orgId],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${orgId}`)
      return data
    },
    enabled: !!orgId,
  })
}

export const useAdminOrgMembers = (orgId: string) => {
  return useQuery<AdminOrgMember[]>({
    queryKey: [ORGS_QUERY_KEY, orgId, 'members'],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${orgId}/members`)
      return data
    },
    enabled: !!orgId,
  })
}

export const useSuspendOrg = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orgId: string) => {
      const { data } = await api.post(`/organizations/${orgId}/suspend`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGS_QUERY_KEY] })
      toast.success('Organización suspendida')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg =
        axiosErr.response?.data?.detail || error.message || 'Error al suspender organización'
      toast.error(msg)
    },
  })
}

export const useUnsuspendOrg = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orgId: string) => {
      const { data } = await api.post(`/organizations/${orgId}/unsuspend`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGS_QUERY_KEY] })
      toast.success('Organización reactivada')
    },
    onError: (error: Error) => {
      const axiosErr = error as Error & { response?: { data?: { detail?: string } } }
      const msg =
        axiosErr.response?.data?.detail || error.message || 'Error al reactivar organización'
      toast.error(msg)
    },
  })
}
